/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useState, useEffect, useRef } from 'react';
import BackButton from '../common/BackButton';
import { useNavigate } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import '../bids/CreateBid.css';

// Import TinyMCE
import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/models/dom';
import 'tinymce/skins/ui/oxide/skin.css';

// Import plugins
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/image';
import 'tinymce/plugins/charmap';
import 'tinymce/plugins/preview';
import 'tinymce/plugins/anchor';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/visualblocks';
import 'tinymce/plugins/code';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/insertdatetime';
import 'tinymce/plugins/media';
import 'tinymce/plugins/table';
import 'tinymce/plugins/help';
import 'tinymce/plugins/wordcount';

const ManagerCompanyForm: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const contactNotesEditorRef = useRef<any>(null);
  const bidFrontPageEditorRef = useRef<any>(null);
  const bidLastPageEditorRef = useRef<any>(null);

  const [companyData, setCompanyData] = useState({
    id: 0,
    name: '',
    email: '',
    phoneNumber: '',
    contactNotes: '',
    bidFrontPage: '',
    bidLastPage: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    loadCompanyData();
  }, []);

  const loadCompanyData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.getManagerCompany();

      if (!response.success || !response.company) {
        setError('Failed to load company data');
        return;
      }

      const company = response.company;
      setCompanyData({
        id: company.id,
        name: company.name,
        email: company.email || '',
        phoneNumber: company.phoneNumber || '',
        contactNotes: company.contactNotes || '',
        bidFrontPage: company.bidFrontPage || '',
        bidLastPage: company.bidLastPage || '',
      });

      // Set TinyMCE editor content
      if (contactNotesEditorRef.current && company.contactNotes) {
        contactNotesEditorRef.current.setContent(company.contactNotes);
      }
      if (bidFrontPageEditorRef.current && company.bidFrontPage) {
        bidFrontPageEditorRef.current.setContent(company.bidFrontPage);
      }
      if (bidLastPageEditorRef.current && company.bidLastPage) {
        bidLastPageEditorRef.current.setContent(company.bidLastPage);
      }
    } catch (err: any) {
      console.error('Error loading company data:', err);
      setError(err.message || 'Failed to load company data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsSaving(true);

    try {
      // Get content from TinyMCE editors
      let contactNotesContent = '';
      let bidFrontPageContent = '';
      let bidLastPageContent = '';

      if (contactNotesEditorRef.current) {
        // Wait for any pending uploads
        if (typeof contactNotesEditorRef.current.uploadImages === 'function') {
          await contactNotesEditorRef.current.uploadImages();
        }
        await new Promise(resolve => setTimeout(resolve, 500));
        contactNotesContent = contactNotesEditorRef.current.getContent();
      }

      if (bidFrontPageEditorRef.current) {
        // Wait for any pending uploads
        if (typeof bidFrontPageEditorRef.current.uploadImages === 'function') {
          await bidFrontPageEditorRef.current.uploadImages();
        }
        await new Promise(resolve => setTimeout(resolve, 500));
        bidFrontPageContent = bidFrontPageEditorRef.current.getContent();
      }

      if (bidLastPageEditorRef.current) {
        // Wait for any pending uploads
        if (typeof bidLastPageEditorRef.current.uploadImages === 'function') {
          await bidLastPageEditorRef.current.uploadImages();
        }
        await new Promise(resolve => setTimeout(resolve, 500));
        bidLastPageContent = bidLastPageEditorRef.current.getContent();
      }

      const request = {
        contactNotes: contactNotesContent,
        bidFrontPage: bidFrontPageContent,
        bidLastPage: bidLastPageContent,
      };

      const response = await apiClient.updateManagerCompany(request);

      if (response.success) {
        setSuccessMessage('Company information updated successfully');
        // Reload data to show sanitized content
        setTimeout(() => loadCompanyData(), 1000);
      } else {
        setError(response.error || 'Failed to update company information');
      }
    } catch (err: any) {
      console.error('Error updating company:', err);
      setError(err.response?.data?.error || err.message || 'An error occurred while updating company information');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard');
  };

  const tinyMceConfig = {
    base_url: '/tinymce',
    suffix: '.min',
    height: 400,
    menubar: true,
    promotion: false,
    plugins: [
      'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
      'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
      'insertdatetime', 'media', 'table', 'help', 'wordcount'
    ],
    toolbar: 'undo redo | blocks | ' +
      'bold italic forecolor | alignleft aligncenter ' +
      'alignright alignjustify | bullist numlist outdent indent | ' +
      'image | removeformat | help',
    content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
    images_upload_handler: (blobInfo: any, progress: any) => new Promise<string>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.withCredentials = false;

      xhr.upload.onprogress = (e) => {
        progress(e.loaded / e.total * 100);
      };

      xhr.onload = () => {
        if (xhr.status === 403) {
          reject({ message: 'HTTP Error: ' + xhr.status, remove: true });
          return;
        }

        if (xhr.status < 200 || xhr.status >= 300) {
          reject('HTTP Error: ' + xhr.status);
          return;
        }

        const json = JSON.parse(xhr.responseText);
        if (!json || typeof json.location != 'string') {
          reject('Invalid JSON: ' + xhr.responseText);
          return;
        }

        resolve(json.location);
      };

      xhr.onerror = () => {
        reject('Image upload failed due to a XHR Transport error. Code: ' + xhr.status);
      };

      const formData = new FormData();
      formData.append('file', blobInfo.blob(), blobInfo.filename());

      xhr.open('POST', '/api/tinymce/upload-image');
      const token = localStorage.getItem('jwt_token');
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);
    }),
    automatic_uploads: true,
    paste_data_images: true,
    images_reuse_filename: false,
    file_picker_types: 'image',
    images_file_types: 'jpg,jpeg,png,gif,webp',
    relative_urls: true,
    remove_script_host: true,
    convert_urls: false
  };

  if (isLoading) {
    return (
      <div className="create-bid-container">
      <BackButton to="/" />
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Loading company information...</p>
        </div>
      </div>
    );
  }

  if (error && !companyData.id) {
    return (
      <div className="create-bid-container">
        <div className="error-message">
          {error}
        </div>
        <button
          type="button"
          onClick={handleCancel}
          className="cancel-button"
          style={{ marginTop: '1rem' }}
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="create-bid-container">
      <div className="create-bid-header">
        <h2>Edit Company Information</h2>
      </div>

      <form onSubmit={handleSubmit} className="create-bid-form">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {successMessage && (
          <div style={{
            padding: '1rem',
            marginBottom: '1rem',
            backgroundColor: '#d4edda',
            border: '1px solid #c3e6cb',
            borderRadius: '6px',
            color: '#155724'
          }}>
            {successMessage}
          </div>
        )}

        <div className="form-group">
          <label>Company Name</label>
          <input
            type="text"
            value={companyData.name}
            readOnly
            disabled
            style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
          />
          <small style={{ color: '#666', fontSize: '0.85rem' }}>
            Contact an administrator to change the company name
          </small>
        </div>

        <div className="form-group">
          <label>Email</label>
          <input
            type="text"
            value={companyData.email}
            readOnly
            disabled
            style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
          />
          <small style={{ color: '#666', fontSize: '0.85rem' }}>
            Contact an administrator to change the email
          </small>
        </div>

        <div className="form-group">
          <label>Phone Number</label>
          <input
            type="text"
            value={companyData.phoneNumber}
            readOnly
            disabled
            style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
          />
          <small style={{ color: '#666', fontSize: '0.85rem' }}>
            Contact an administrator to change the phone number
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="contactNotes">Contact Notes</label>
          <Editor
            onInit={(evt, editor) => contactNotesEditorRef.current = editor}
            licenseKey="gpl"
            initialValue={companyData.contactNotes}
            init={tinyMceConfig}
            disabled={isSaving}
          />
        </div>

        <div className="form-group">
          <label htmlFor="bidFrontPage">Bid Front Page</label>
          <Editor
            onInit={(evt, editor) => bidFrontPageEditorRef.current = editor}
            licenseKey="gpl"
            initialValue={companyData.bidFrontPage}
            init={tinyMceConfig}
            disabled={isSaving}
          />
        </div>

        <div className="form-group">
          <label htmlFor="bidLastPage">Bid Last Page</label>
          <Editor
            onInit={(evt, editor) => bidLastPageEditorRef.current = editor}
            licenseKey="gpl"
            initialValue={companyData.bidLastPage}
            init={tinyMceConfig}
            disabled={isSaving}
          />
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSaving}
            className="cancel-button"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="submit-button"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ManagerCompanyForm;
